// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Loader, Paper, Stack, Textarea, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, formatDate, getExtension, normalizeErrorString } from '@medplum/core';
import type {
  Claim,
  Condition,
  Coverage,
  Encounter,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Reference,
  RelatedPerson,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { Cms1500AutofillInput, Cms1500Form } from '@polarissleep/shared';
import { autofillCms1500Form, MRN_SYSTEM, readCms1500Form, resolvePrice, writeCms1500Form } from '@polarissleep/shared';
import { IconArrowLeft, IconInfoCircle } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { CLAIM_PATIENT_PAID_URL, CLAIM_PLACE_OF_SERVICE_URL } from '../../../bots/services/claims/constants';
import { ClaimAttachmentsSection } from '../../components/claims/ClaimAttachmentsSection';
import { ClaimMedicalInfoSection } from '../../components/claims/ClaimMedicalInfoSection';
import { ClaimStatusBadge } from '../../components/claims/ClaimStatusBadge';
import {
  CLAIM_TASK_SYSTEM,
  CLAIM_VALIDATION_TASK_CODE,
  type ClaimValidationIssue,
  isOpenClaimTask,
  parseValidationIssues,
} from '../../components/claims/claimTasks';
import { DraftClaimEditor, type DraftClaimEditorHandle } from '../../components/claims/DraftClaimEditor';
import { botIdentifier } from '../../lib/bot-identifier';
import { findActiveChargeItemDefinitionByCpt } from '../../utils/chargeitems';
import { showErrorNotification } from '../../utils/notifications';
import classes from './ClaimDetailPage.module.css';

interface SubmitClaimResult {
  status: 'valid' | 'invalid' | 'submitted' | 'failed' | 'transmission-disabled';
  issues: ClaimValidationIssue[];
  claimStatus: Claim['status'];
  message?: string;
}

interface ClaimContextData {
  patient?: Patient;
  encounter?: Encounter;
  tasks: Task[];
}

type MedplumClientLike = ReturnType<typeof useMedplum>;

const INTERNAL_NOTE_URL = 'http://polarissleep.com/fhir/extensions/internal-note';
const STEDI_PAYER_NETWORK_SYSTEM = 'https://www.stedi.com/healthcare/network';
const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm';

/**
 * Issues only the bot can raise (form issues are re-checked in the editor). Validation Tasks written
 * before claims carried a CMS-1500 form hold other fields (PractitionerRole.specialty, Claim.item…)
 * that no longer block submission, so those are dropped.
 */
const SERVER_ISSUE_FIELDS = new Set(['Coverage.type', 'ClaimContext']);

function isServerIssue(issue: ClaimValidationIssue): boolean {
  return SERVER_ISSUE_FIELDS.has(issue.field);
}

/** Insurance price for a CPT code from the chargemaster, or undefined when it has none. */
async function chargemasterPrice(medplum: MedplumClientLike, cpt: string): Promise<number | undefined> {
  const definition = await findActiveChargeItemDefinitionByCpt(medplum, cpt).catch(() => undefined);
  try {
    return definition ? resolvePrice(definition, 'insurance') : undefined;
  } catch {
    return undefined;
  }
}

async function readOptional<T>(promise: Promise<T>): Promise<T | undefined> {
  try {
    return await promise;
  } catch {
    return undefined;
  }
}

function readRef<T>(medplum: MedplumClientLike, reference: Reference | undefined): Promise<T | undefined> {
  return reference?.reference
    ? readOptional(medplum.readReference(reference) as unknown as Promise<T>)
    : Promise.resolve(undefined);
}

/**
 * Prefill a form for a draft saved before claims carried one, from the same records the claim
 * bot would use: patient, coverage, payer, rendering practitioner, billing org and the Claim's
 * own diagnoses and items.
 */
async function autofillLegacyClaim(
  medplum: MedplumClientLike,
  claim: Claim,
  payor: Organization | undefined,
  { patient, encounter }: ClaimContextData
): Promise<Cms1500Form> {
  const practitionerRef = claim.careTeam?.find((member) =>
    member.provider?.reference?.startsWith('Practitioner/')
  )?.provider;
  const [coverage, practitioner, billingOrganization] = await Promise.all([
    readRef<Coverage>(medplum, claim.insurance?.[0]?.coverage),
    readRef<Practitioner>(medplum, practitionerRef),
    claim.provider?.reference?.startsWith('Organization/')
      ? readRef<Organization>(medplum, claim.provider)
      : Promise.resolve(undefined),
  ]);
  const subscriberRef = coverage?.subscriber;
  const subscriber =
    subscriberRef?.reference && subscriberRef.reference !== claim.patient?.reference
      ? await readRef<Patient | RelatedPerson>(medplum, subscriberRef)
      : undefined;
  const practitionerRole = practitioner?.id
    ? (
        await readOptional(
          medplum.searchResources('PractitionerRole', { practitioner: `Practitioner/${practitioner.id}` })
        )
      )?.[0]
    : undefined;
  const diagnoses = await Promise.all(
    (claim.diagnosis ?? []).map(async (diagnosis) => {
      const coded = diagnosis.diagnosisCodeableConcept?.coding?.find((coding) => coding.code)?.code;
      if (coded) {
        return coded;
      }
      const condition = await readRef<Condition>(medplum, diagnosis.diagnosisReference);
      return condition?.code?.coding?.find((coding) => coding.system === ICD10_SYSTEM)?.code ?? '';
    })
  );
  const input: Cms1500AutofillInput = {
    patient,
    subscriber,
    coverage,
    payor,
    practitioner,
    practitionerRole: practitionerRole as PractitionerRole | undefined,
    billingOrganization,
    encounter,
    diagnoses,
    lines: await Promise.all(
      (claim.item ?? [])
        .filter((item) => item.productOrService?.coding?.some((coding) => coding.code))
        .map(async (item) => {
          const cpt = item.productOrService?.coding?.find((coding) => coding.code)?.code ?? '';
          return {
            cpt,
            modifiers: (item.modifier ?? []).map((modifier) => modifier.coding?.[0]?.code ?? '').filter(Boolean),
            units: item.quantity?.value,
            // Placeholder lines on insured drafts carry the visit's CPT code but no price.
            charge: item.net?.value ?? item.unitPrice?.value ?? (await chargemasterPrice(medplum, cpt)),
          };
        })
    ),
    placeOfServiceCode: getExtension(claim, CLAIM_PLACE_OF_SERVICE_URL)?.valueCode,
    patientPaid: getExtension(claim, CLAIM_PATIENT_PAID_URL)?.valueMoney?.value,
  };
  return autofillCms1500Form(input);
}

function taskCode(task: Task): string {
  return task.code?.coding?.find((coding) => coding.system === CLAIM_TASK_SYSTEM)?.code ?? task.code?.text ?? 'task';
}

export function ClaimDetailPage(): JSX.Element {
  const { claimId } = useParams<{ claimId: string }>();
  const medplum = useMedplum();
  const [claim, setClaim] = useState<Claim | undefined>();
  const [initialForm, setInitialForm] = useState<Cms1500Form | undefined>();
  const [context, setContext] = useState<ClaimContextData>({ tasks: [] });
  const [payers, setPayers] = useState<Organization[]>([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverIssues, setServerIssues] = useState<ClaimValidationIssue[]>([]);
  const editorRef = useRef<DraftClaimEditorHandle>(null);

  const loadTasks = useCallback(
    async (id: string): Promise<Task[]> =>
      (await readOptional(medplum.searchResources('Task', { focus: `Claim/${id}`, _sort: '-_lastUpdated' }))) ?? [],
    [medplum]
  );

  useEffect(() => {
    if (!claimId) {
      return;
    }
    setLoading(true);
    medplum
      .readResource('Claim', claimId)
      .then(async (c: Claim) => {
        const encounterRef = c.item?.find((item) => item.encounter?.length)?.encounter?.[0];
        const [tasks, insurer, stediPayers, patient, encounter] = await Promise.all([
          loadTasks(claimId),
          readRef<Organization>(medplum, c.insurer),
          readOptional(medplum.searchResources('Organization', { identifier: `${STEDI_PAYER_NETWORK_SYSTEM}|` })),
          readRef<Patient>(medplum, c.patient),
          readRef<Encounter>(medplum, encounterRef),
        ]);
        const data: ClaimContextData = { patient, encounter, tasks };
        const form = readCms1500Form(c) ?? (await autofillLegacyClaim(medplum, c, insurer, data));
        const payerList: Organization[] = [...(stediPayers ?? [])];
        if (insurer?.id && !payerList.some((payer) => payer.id === insurer.id)) {
          payerList.unshift(insurer);
        }
        const validationTask = tasks.find(
          (task) => taskCode(task) === CLAIM_VALIDATION_TASK_CODE && isOpenClaimTask(task)
        );
        setClaim(c);
        setContext(data);
        setPayers(payerList);
        setInitialForm(form);
        setNote(getExtension(c, INTERNAL_NOTE_URL)?.valueString ?? '');
        setServerIssues(parseValidationIssues(validationTask).filter(isServerIssue));
      })
      .catch(showErrorNotification)
      .finally(() => setLoading(false));
  }, [medplum, claimId, loadTasks]);

  const refreshTasks = (): void => {
    if (claimId) {
      loadTasks(claimId)
        .then((tasks) => setContext((current) => ({ ...current, tasks })))
        .catch(() => undefined);
    }
  };

  const saveClaim = async (): Promise<void> => {
    const editor = editorRef.current;
    if (!claim?.id || !editor) {
      return;
    }
    const payer = payers.find((candidate) => candidate.id === editor.getPayerOrganizationId());
    const base: Claim = {
      ...claim,
      insurer: payer ? createReference(payer) : claim.insurer,
      extension: [
        ...(claim.extension ?? []).filter((extension) => extension.url !== INTERNAL_NOTE_URL),
        ...(note ? [{ url: INTERNAL_NOTE_URL, valueString: note }] : []),
      ],
    };
    setClaim(await medplum.updateResource(writeCms1500Form(base, editor.getForm())));
  };

  /** Run the submit-claim bot. A dry run re-validates server-side and refreshes the coordinator task. */
  const runBot = async (dryRun: boolean): Promise<SubmitClaimResult | undefined> => {
    if (!claimId) {
      return undefined;
    }
    try {
      const result = (await medplum.executeBot(botIdentifier('submit-claim'), {
        claimId,
        dryRun,
      })) as SubmitClaimResult;
      setServerIssues((result.issues ?? []).filter(isServerIssue));
      setClaim((prev) => (prev ? { ...prev, status: result.claimStatus } : prev));
      return result;
    } catch (err) {
      const technicalMessage = normalizeErrorString(err).toLowerCase();
      const missingEncounter = technicalMessage.includes('encounter') && technicalMessage.includes('linked');
      setServerIssues([
        {
          field: missingEncounter ? 'Encounter' : 'Claim',
          box: 'Claim',
          message: missingEncounter
            ? 'This draft is missing a linked encounter. Finish the encounter before submitting it.'
            : 'The server could not check this claim. Try again.',
        },
      ]);
      return undefined;
    } finally {
      refreshTasks();
    }
  };

  const handleSaveDraft = async (): Promise<void> => {
    setSaving(true);
    try {
      await saveClaim();
      const result = await runBot(true);
      showNotification({
        color: 'green',
        message: result?.status === 'valid' ? 'Draft saved — ready to submit' : 'Draft saved',
      });
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    setSubmitting(true);
    try {
      const localIssues = editor.revealErrors();
      await saveClaim();
      if (localIssues.length > 0) {
        showNotification({
          color: 'red',
          message: `Fix ${localIssues.length} validation error${localIssues.length === 1 ? '' : 's'} before submitting`,
        });
        return;
      }
      const result = await runBot(false);
      if (result?.status === 'submitted') {
        showNotification({ color: 'green', message: 'Claim submitted to payer' });
      } else if (result?.status === 'transmission-disabled') {
        showNotification({ color: 'yellow', message: result.message ?? 'Claim transmission is disabled' });
      } else {
        editor.revealErrors();
        showNotification({ color: 'red', message: result?.message ?? 'Submission failed — see the validation errors' });
      }
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  const { patient, encounter, tasks } = context;
  const readOnly = claim?.status === 'active' || claim?.status === 'cancelled';
  const patientName = [patient?.name?.[0]?.given?.join(' '), patient?.name?.[0]?.family].filter(Boolean).join(' ');
  const mrn = patient?.identifier?.find((identifier) => identifier.system === MRN_SYSTEM)?.value;

  return (
    <div className={classes.root}>
      <Link to="/billing/claims" className={classes.back}>
        <IconArrowLeft size={15} />
        All claims
      </Link>

      <div className={classes.head}>
        <div className={classes.titleRow}>
          <h2>Claim {claimId}</h2>
          {claim && <ClaimStatusBadge status={claim.status} />}
        </div>
        <div className={classes.meta}>
          {patientName && (
            <span>
              <b>{patientName}</b>
              {mrn ? ` · MRN ${mrn}` : ''}
            </span>
          )}
          {encounter?.period?.start && <span>Date of service {formatDate(encounter.period.start)}</span>}
          <span>Professional · claim</span>
          {claim?.created && <span>Created {formatDate(claim.created)}</span>}
        </div>
      </div>

      {claim?.status === 'draft' && (
        <div className={classes.manual}>
          <IconInfoCircle size={19} />
          <div>
            <b>This is the CMS-1500 Stedi transmits as an 837P.</b> Fields validate as you edit using Stedi's required
            fields and 5010 rules; a clean claim transmits and moves to <code>active</code>. Payer responses arrive on
            their own — 277CA acknowledgment or rejection first, then the 835 remittance — and open a task when they
            need attention.
          </div>
        </div>
      )}

      {tasks.length > 0 && (
        <section className={classes.section}>
          <Title order={2} className={classes.sectionTitle}>
            Tasks
          </Title>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Task code</th>
                <th>Opened</th>
                <th>Detail</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td>
                    <code className={classes.code}>{taskCode(task)}</code>
                  </td>
                  <td>{task.authoredOn ? formatDate(task.authoredOn) : '—'}</td>
                  <td>{task.description ?? '—'}</td>
                  <td>
                    <span className={isOpenClaimTask(task) ? classes.open : classes.done}>
                      {isOpenClaimTask(task) ? 'Open' : 'Completed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {claim && initialForm && (
        <DraftClaimEditor
          key={claim.id}
          ref={editorRef}
          initialForm={initialForm}
          payers={payers}
          initialPayerOrganizationId={claim.insurer?.reference?.split('/')[1]}
          readOnly={readOnly}
          extraIssues={serverIssues}
          saving={saving}
          submitting={submitting}
          onSaveDraft={handleSaveDraft}
          onSubmit={handleSubmit}
        />
      )}

      <Stack gap="sm">
        {patient && <ClaimMedicalInfoSection patient={patient} encounter={encounter} />}
        {patient?.id && claimId && <ClaimAttachmentsSection claimId={claimId} patientId={patient.id} />}
        <Paper withBorder p="md">
          <Title order={3} mb="xs">
            Internal note
          </Title>
          <Textarea
            value={note}
            readOnly={readOnly}
            onChange={(event) => setNote(event.currentTarget.value)}
            placeholder="Context for whoever submits this — not sent to the payer. Saved with the draft."
            minRows={3}
          />
        </Paper>
      </Stack>
    </div>
  );
}
